import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  createZadProduct,
  fetchZadProductById,
  fetchZadProducts,
  generateZadProductBarcode,
  importZadProducts,
  restoreZadProduct,
  updateZadProduct,
  updateZadProductStatus,
} from './service'
import {ZadProduct} from '../../../models/zad'
import {FetchZadProductsResponse, ZadProductState} from './types'

const initialState: ZadProductState = {
  products: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadProductState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadProductState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertProduct(state: ZadProductState, product?: ZadProduct | null) {
  if (!product?._id) return
  const index = state.products.findIndex((item) => item._id === product._id)
  if (index >= 0) state.products[index] = product
  else state.products.unshift(product)
}

const zadProductSlice = createSlice({
  name: 'zadProducts',
  initialState,
  reducers: {
    clearZadProductError: (state: ZadProductState) => {
      clearError(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadProducts.pending, (state: ZadProductState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadProducts.rejected, (state: ZadProductState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadProducts.fulfilled,
      (state: ZadProductState, action: PayloadAction<FetchZadProductsResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.products = action.payload.products
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadProductById.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadProductById.rejected, (state: ZadProductState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadProductById.fulfilled, (state: ZadProductState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertProduct(state, action.payload.product)
    })

    builder.addCase(createZadProduct.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadProduct.rejected, (state: ZadProductState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadProduct.fulfilled, (state: ZadProductState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadProduct.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(updateZadProduct.rejected, (state: ZadProductState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(updateZadProduct.fulfilled, (state: ZadProductState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadProductStatus.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadProductStatus.rejected,
      (state: ZadProductState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadProductStatus.fulfilled, (state: ZadProductState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(importZadProducts.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(importZadProducts.rejected, (state: ZadProductState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(importZadProducts.fulfilled, (state: ZadProductState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(generateZadProductBarcode.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      generateZadProductBarcode.rejected,
      (state: ZadProductState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(generateZadProductBarcode.fulfilled, (state: ZadProductState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertProduct(state, action.payload.product)
    })

    builder.addCase(restoreZadProduct.pending, (state: ZadProductState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(restoreZadProduct.rejected, (state: ZadProductState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(restoreZadProduct.fulfilled, (state: ZadProductState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertProduct(state, action.payload.product)
    })
  },
})

export default zadProductSlice.reducer
export const actions = zadProductSlice.actions
export {initialState as zadProductInitialState}
